import { useState, useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { Button, Icon, Progress } from '@/components/ui';
import { useUploadDocument } from '@/hooks/use-knowledge';

// =============================================================================
// Types
// =============================================================================

interface DocumentUploadProps {
  collectionId: string;
  onUploadComplete?: () => void;
  onCancel?: () => void;
}

interface FileWithPreview {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  progress: number;
  error?: string;
}

// =============================================================================
// Constants
// =============================================================================

const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// =============================================================================
// Helper Functions
// =============================================================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

// =============================================================================
// Document Upload Component
// =============================================================================

export function DocumentUpload({ 
  collectionId, 
  onUploadComplete,
  onCancel,
}: DocumentUploadProps) {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const uploadDocument = useUploadDocument(collectionId);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    // Add accepted files
    const newFiles: FileWithPreview[] = acceptedFiles.map((file) => ({
      file,
      id: generateId(),
      status: 'pending' as const,
      progress: 0,
    }));

    // Handle rejected files
    const rejectedWithErrors: FileWithPreview[] = rejectedFiles.map(({ file, errors }) => ({
      file,
      id: generateId(),
      status: 'error' as const,
      progress: 0,
      error: errors[0]?.message || 'File rejected',
    }));

    setFiles((prev) => [...prev, ...newFiles, ...rejectedWithErrors]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    disabled: isUploading,
  });

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpload = async () => {
    const pendingFiles = files.filter((f) => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setIsUploading(true);

    for (const fileItem of pendingFiles) {
      // Update status to uploading
      setFiles((prev) => 
        prev.map((f) => 
          f.id === fileItem.id 
            ? { ...f, status: 'uploading' as const, progress: 0 }
            : f
        )
      );

      try {
        // Simulate progress (actual progress would come from axios onUploadProgress)
        const progressInterval = setInterval(() => {
          setFiles((prev) =>
            prev.map((f) =>
              f.id === fileItem.id && f.status === 'uploading'
                ? { ...f, progress: Math.min(f.progress + 10, 90) }
                : f
            )
          );
        }, 200);

        await uploadDocument.mutateAsync({ file: fileItem.file });

        clearInterval(progressInterval);

        // Update status to complete
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileItem.id
              ? { ...f, status: 'complete' as const, progress: 100 }
              : f
          )
        );
      } catch (error) {
        // Update status to error
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileItem.id
              ? { 
                  ...f, 
                  status: 'error' as const, 
                  error: error instanceof Error ? error.message : 'Upload failed',
                }
              : f
          )
        );
      }
    }

    setIsUploading(false);
    
    // Check if all files completed successfully
    const allComplete = files.every((f) => f.status === 'complete' || f.status === 'error');
    if (allComplete && onUploadComplete) {
      setTimeout(onUploadComplete, 500);
    }
  };

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const hasErrors = files.some((f) => f.status === 'error');
  const allComplete = files.length > 0 && files.every((f) => f.status === 'complete');

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer
          ${isDragActive 
            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' 
            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
          }
          ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        <Icon 
          name="upload" 
          size={40} 
          className={`mx-auto mb-4 ${isDragActive ? 'text-blue-500' : 'text-gray-400'}`} 
        />
        {isDragActive ? (
          <p className="text-blue-600 dark:text-blue-400 font-medium">Drop files here...</p>
        ) : (
          <>
            <p className="text-gray-900 dark:text-gray-100 font-medium mb-1">
              Drag & drop files here
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              or click to select files
            </p>
          </>
        )}
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
          PDF, TXT, MD, DOCX • Max 10MB per file
        </p>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {files.map((fileItem) => (
            <div 
              key={fileItem.id}
              className={`
                flex items-center gap-3 p-3 rounded-lg border
                ${fileItem.status === 'error' 
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                  : fileItem.status === 'complete'
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }
              `}
            >
              {/* Icon */}
              <div className="flex-shrink-0">
                {fileItem.status === 'complete' ? (
                  <Icon name="check-circle" size={20} className="text-green-500" />
                ) : fileItem.status === 'error' ? (
                  <Icon name="alert-circle" size={20} className="text-red-500" />
                ) : fileItem.status === 'uploading' ? (
                  <Icon name="loader" size={20} className="text-blue-500 animate-spin" />
                ) : (
                  <Icon name="file" size={20} className="text-gray-400" />
                )}
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {fileItem.file.name}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(fileItem.file.size)}
                  </span>
                  {fileItem.error && (
                    <span className="text-xs text-red-500">{fileItem.error}</span>
                  )}
                </div>
                {fileItem.status === 'uploading' && (
                  <Progress value={fileItem.progress} className="h-1 mt-1" />
                )}
              </div>

              {/* Remove Button */}
              {fileItem.status !== 'uploading' && (
                <button
                  onClick={() => removeFile(fileItem.id)}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                  disabled={isUploading}
                >
                  <Icon name="x" size={16} className="text-gray-400" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button 
          variant="outline" 
          onClick={onCancel}
          disabled={isUploading}
        >
          Cancel
        </Button>
        {allComplete ? (
          <Button onClick={onUploadComplete}>
            Done
          </Button>
        ) : (
          <Button 
            onClick={handleUpload}
            disabled={pendingCount === 0 || isUploading}
          >
            {isUploading ? (
              <>
                <Icon name="loader" size={16} className="animate-spin mr-2" />
                Uploading...
              </>
            ) : (
              <>Upload {pendingCount > 0 && `(${pendingCount})`}</>
            )}
          </Button>
        )}
      </div>

      {/* Error Summary */}
      {hasErrors && !isUploading && (
        <p className="text-sm text-red-500 text-center">
          Some files could not be uploaded. Please review the errors above.
        </p>
      )}
    </div>
  );
}
